// @flow
import type { DataField } from "../../lib/Types";
import React from "react";
import { observer } from "mobx-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import Input from "./Input";
import {
  objectIdentity,
  unregisterDatePicker,
  requestClose,
  requestOpen,
} from "./date_picker_manager";
import { closeAndRefocusPicker, registerManagedDatePicker } from "./managed_date_picker";

type Props = {
  data_field: DataField,
  sample: any,
  fullWidth: boolean,
  placeholder: ?string,
  onFocus: Function,
  copyDown: Function,
  gridCol: ?string,
};

class TextInput extends React.Component<Props> {
  pickerRef: any;
  pickerKey: ?string;
  skipNextOpenOnFocus: boolean = false;

  constructor(props: Props) {
    super(props);
    this.pickerRef = React.createRef();
  }

  componentDidMount() {
    this._registerDatePickerIfNeeded(this.props);
  }

  componentDidUpdate(prevProps: Props) {
    const prevKey = this._computePickerKey(prevProps);
    const nextKey = this._computePickerKey(this.props);
    if (prevKey !== nextKey) {
      if (prevKey) unregisterDatePicker(prevKey);
      this._registerDatePickerIfNeeded(this.props);
    }
  }

  componentWillUnmount() {
    if (this.pickerKey) unregisterDatePicker(this.pickerKey);
  }

  _computePickerKey = (props: Props): ?string => {
    const { data_field, sample } = props;
    if (!data_field || !data_field.data_field_name || !data_field.data_field_name.name) return null;
    if (!data_field.data_field_name.name.match(/date/i)) return null;

    // Prefer a stable key derived from the sample + data_field_name id.
    const samplePart = sample ? objectIdentity(sample) : objectIdentity(data_field);
    const dfnId = data_field.data_field_name.id || objectIdentity(data_field.data_field_name);
    return `data_field_date::${dfnId}::${samplePart}`;
  };

  _registerDatePickerIfNeeded = (props: Props) => {
    const key = this._computePickerKey(props);
    this.pickerKey = key;
    if (!key) return;

    registerManagedDatePicker({ key, pickerRef: this.pickerRef });
  };

  updateValueFromDate = (date) => {
    this.props.data_field.value = date ? moment(date).format("DD/MM/YYYY") : null;
    this.forceUpdate();

    // Keep focus after selecting a date so keyboard navigation continues.
    // Avoid re-opening loop by skipping the next onFocus-triggered open.
    if (this.pickerKey) {
      closeAndRefocusPicker({
        key: this.pickerKey,
        pickerRef: this.pickerRef,
        setSkipNextOpen: (v) => {
          this.skipNextOpenOnFocus = v;
        },
      });
    }
  };
  updateValue = (event) => {
    this.props.data_field.value = event.target.value;
  };

  _handleKeyDown = (e) => {
    const { onCtrlDown, onCtrlUp } = this.props;
    if (e.ctrlKey && e.keyCode === 68) {
      console.log("Ctrl + Down pressed");
      onCtrlDown && onCtrlDown();
      e.preventDefault();
    }
  };

  render() {
    const {
      fullWidth,
      placeholder,
      data_field: { mandatory, highlighted, data_field_name },
      onFocus,
      gridCol,
    } = this.props;
    var value = this.props.data_field.value;

    var dataType;
    if (data_field_name.name.match(/Time Sampled|Manufacturing Time/i)) dataType = "time";
    else if (data_field_name.name.match(/date/i)) dataType = "date";
    else {
      dataType = "text";
    }

    if (dataType == "text") {
      return (
        <Input
          placeholder={placeholder}
          value={value}
          required={mandatory}
          onChange={this.updateValue}
          fullWidth={fullWidth}
          highlighted={highlighted}
          onFocus={onFocus}
          style={{ flex: 1 }}
          onKeyDown={this._handleKeyDown}
          data-sample-grid-col={gridCol}
        />
      );
    }

    if (dataType == "date")
      return (
        <div style={{ flex: 1, backgroundColor: mandatory && !value ? "#FFBBBB" : "" }}>
          <DatePicker
            ref={this.pickerRef}
            customInput={
              <Input
                placeholder={placeholder}
                required={mandatory}
                fullWidth={fullWidth}
                highlighted={highlighted}
                data-sample-grid-col={gridCol}
                data-sample-grid-datepicker="true"
                data-date-picker-key={this.pickerKey || undefined}
              />
            }
            value={value}
            onChange={this.updateValueFromDate}
            dateFormat="dd/MM/yyyy"
            preventOpenOnFocus={true}
            onFocus={(e) => {
              onFocus && onFocus(e);
              if (!this.pickerKey) return;
              if (this.skipNextOpenOnFocus) {
                this.skipNextOpenOnFocus = false;
                return;
              }
              requestOpen(this.pickerKey);
            }}
            onInputClick={() => this.pickerKey && requestOpen(this.pickerKey)}
            onBlur={() => this.pickerKey && requestClose(this.pickerKey)}
            onKeyDown={this._handleKeyDown}
            isClearable={true}
            onClickOutside={() => this.pickerKey && requestClose(this.pickerKey)}
            onChangeRaw={(e) => {
              if (!e.target.value) this.updateValue(e);
            }}
            showYearDropdown
          />
        </div>
      );

    if (dataType == "time") {
      var fade = false;
      if (value == null || value === "") {
        value = "00:00";
        fade = true;
      }

      return (
        <Input
          type="time"
          step={300}
          pattern={"[0-9]{2}:[0-9]{2}"}
          placeholder={placeholder}
          value={value}
          required={mandatory}
          onChange={this.updateValue}
          fullWidth={fullWidth}
          highlighted={highlighted}
          onFocus={onFocus}
          style={{ flex: 1, opacity: fade ? 0.4 : 1 }}
          onKeyDown={this._handleKeyDown}
          data-sample-grid-col={gridCol}
        />
      );
    }
  }
}

export default observer(TextInput);
